import { Component, Input, OnInit } from '@angular/core';
import { AppComponent } from 'app/app.component';
import { ILine } from 'app/_models/ILine';
import { IHeader } from 'app/_models/IHeader';
import * as XLSX from 'xlsx';
import { GridService } from 'app/_services/grid.service';
import { IGridData } from 'app/_models/IGridData';
import { ISettings } from 'app/_models/ISettings';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import parse from 'node-html-parser';
import { IconDefinition, faFileExport } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'export-component',
  templateUrl: './export.component.html',
  styleUrls: ['./export.component.css']
})
export class ExportComponent implements OnInit {
  //#region Fields & Icons
  @Input() settings: ISettings;  
  @Input() lines: ILine[] = [];
  @Input() headers: IHeader[] = [];
  @Input() mobileView: boolean;
  @Input() mapView: boolean;
  @Input() userId: string;
  @Input() nodeId: string;
  @Input() viewName: string;
  @Input() keyValue: string;
  @Input() filterString: string;
  @Input() username: string;
  exportFormat: string = 'Excel (.xlsx)';     
  exportAll: boolean = false;
  fileName: string;
  faFileExport: IconDefinition = faFileExport;
  //#endregion

  constructor(
    public app: AppComponent,    
    private gridService: GridService
  ) { }

  ngOnInit(): void { 
    if (this.viewName == 'master')  this.keyValue = this.nodeId;    
  }

  selectFormat(event: any): void {
    this.exportFormat = event.srcElement.value.toString();
  }

  nameFile(event: any): void {
    this.fileName = event.srcElement.value.toString();
  }

  validateExport(): void {
    if (!this.fileName) {
      this.app.alertError(this.app.translations.ALERT_Error_NoFileName);
      return;
    }

    this.app.showLoader();
    this.app.loaderText = `${this.app.translations.GBL_lbl_GettingData}...`;
    if (this.exportAll) {
      this.getAllData();
      return;
    }
    
    this.exportData();   
  }

  exportData(): void {
    try {
      if (this.exportFormat == 'Excel (.xlsx)') {
        let headers = this.headers.filter(header => header.visible);
        let headerCaptions = headers.map(header => this.mapView ? header.field : header.caption);

        let rows = this.lines.map(item => {
          return headerCaptions.map(caption => item.list[caption] ?? '');
        });                     
     
        let worksheetData = [headerCaptions, ...rows];
        let worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        let colWidths = headerCaptions.map((_, colIndex) => {
          let colValues = [headerCaptions[colIndex], ...rows.map(row => row[colIndex])];
          let maxLength = colValues.reduce((max, val) => {
            let str = val != null ? String(val) : '';
            return Math.max(max, str.length);
          }, 10); 
          return { wch: Math.min(maxLength + 2, 50) }; 
        });
        worksheet['!cols'] = colWidths;
              
        for (let r = 1; r <= rows.length; r++) {
          for (let c = 0; c < headers.length; c++) {
            let header = headers[c];
            let cellAddress = XLSX.utils.encode_cell({ r, c });
            let cell = worksheet[cellAddress];
            if (!cell || cell.v === '') continue;

            if (header.dataType === 'Int32') {              
              cell.t = 'n';
              cell.z = '0';
              cell.v = Number(cell.v);
            }

            if (header.dataType === 'Decimal' || header.dataType === 'Double') {              
              cell.t = 'n';
              cell.z = '0.00';
              cell.v = Number(cell.v);
            }

            if (header.displayFormat === 'DATETIME') { 
              let dayjsFormat = this.settings.dateFormatSetting.toUpperCase();              
              let parsed = dayjs(cell.v, dayjsFormat + ' HH:mm', true);                
              if (parsed.isValid()) {
                cell.t = 'd';
                cell.v = parsed.toDate();
                let format = this.settings.dateFormatSetting.toLowerCase();
                cell.z = `${format} HH:mm:ss`;
              }            
            }

            if (header.displayFormat === 'DATE') { 
              let dayjsFormat = this.settings.dateFormatSetting.toUpperCase();              
              let parsed = dayjs(cell.v, dayjsFormat, true);                
              if (parsed.isValid()) {
                cell.t = 'd';
                cell.v = parsed.toDate();
                let format = this.settings.dateFormatSetting.toLowerCase();
                cell.z = format;
              }  
            }

            if (header.dataType === 'Boolean') {   
              let boolVal = cell.v === true || cell.v === 'true' || cell.v === 'TRUE' || cell.v === 1;           
              cell.t = 'b';
              cell.v = boolVal;
            }

            if (header.dataType === 'HTML') {
              let parsedValue = !cell.v.toString().includes('</div>') ? cell.v.toString() : parse(cell.v.toString()).querySelector('div')?.text || '';
              cell.v = parsedValue;
            }
          }
        }        

        let workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, `${this.fileName}.xlsx`);
        this.app.hideLoader();
      }
    } catch (error) { this.app.alertError(error); } 
  }

  getAllData(): void {  
    let keys = ['userId', 'nodeId', 'viewName', 'username', 'keyValue', 'filterString'];
    let values = [this.userId, this.nodeId, this.viewName, this.username, this.keyValue, this.filterString];
    let formData = this.app.buildForm(keys, values);
    this.gridService.getDataForExport(formData).subscribe({
      next: (data: IGridData) => {        
        this.headers = data.headers;
        this.lines = data.lines;        
        this.exportData();  
      }, error(err) { console.log(err); },
    })
  }

  toggleExportAll(event: any): void {
    this.exportAll = event.checked;
  }   
}
